import React, { useState, useEffect } from 'react';
import {
    Plus,
    Trash2,
    Save,
    Edit3,
    MessageCircle,
    Eye,
    Database,
    X,
    Clipboard,
    LayoutList
} from 'lucide-react';
import { useChatbotContext } from '../../../../../../context/ChatbotContext';
import { auth } from '../../../../../../firebase/config/config';
import FormSubmissions from './FormSubmissions';
import logger from '../../../../../../utils/logger';

function Forms({ selectedChatbotId: propSelectedChatbotId, onFormsChange }) {
    const { chatbots } = useChatbotContext();
    const [selectedChatbotId, setSelectedChatbotId] = useState(propSelectedChatbotId || '');
    const [activeTab, setActiveTab] = useState('builder');
    const [forms, setForms] = useState([]);

    // Propagate forms changes to parent
    useEffect(() => {
        if (onFormsChange) {
            onFormsChange(forms);
        }
    }, [forms, onFormsChange]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const [newForm, setNewForm] = useState({
        prompt: '',
        fields: [{ name: '', type: 'text', required: true }],
        step: 0,
        enabled: true
    });

    // Update selectedChatbotId when prop changes
    useEffect(() => {
        if (propSelectedChatbotId) {
            setSelectedChatbotId(propSelectedChatbotId);
        }
    }, [propSelectedChatbotId]);

    // Auto-select first chatbot when chatbots are loaded (only if no prop provided)
    useEffect(() => {
        if (chatbots.length > 0 && !selectedChatbotId && !propSelectedChatbotId) {
            setSelectedChatbotId(chatbots[0].chatbot_id);
        }
    }, [chatbots, selectedChatbotId, propSelectedChatbotId]);

    // Load forms from backend for selected chatbot
    useEffect(() => {
        if (selectedChatbotId) {
            loadFormsFromBackend(selectedChatbotId);
        }
    }, [selectedChatbotId]);

    // Load forms from backend
    const loadFormsFromBackend = async (chatbotId) => {
        try {
            const response = await fetch(`/api/forms?chatbotId=${chatbotId}`);
            if (response.ok) {
                const data = await response.json();
                const formsData = data.forms || [];
                setForms(formsData);
                localStorage.setItem(`chatbotForms_${chatbotId}`, JSON.stringify(formsData));
            } else {
                const savedForms = localStorage.getItem(`chatbotForms_${chatbotId}`);
                if (savedForms) {
                    try {
                        setForms(JSON.parse(savedForms));
                    } catch (error) {
                        logger.error('Error loading forms from localStorage:', error);
                        setForms([]);
                    }
                } else {
                    setForms([]);
                }
            }
        } catch (error) {
            logger.error('Error loading forms from backend:', error);
            const savedForms = localStorage.getItem(`chatbotForms_${chatbotId}`);
            if (savedForms) {
                try {
                    setForms(JSON.parse(savedForms));
                } catch (error) {
                    setForms([]);
                }
            } else {
                setForms([]);
            }
        }
    };

    // Save forms to backend
    const saveFormsToBackend = async () => {
        if (!selectedChatbotId) {
            alert('Please select a chatbot first');
            return;
        }

        setIsSaving(true);
        setSaveStatus('');

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch('/api/forms', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : '',
                },
                body: JSON.stringify({
                    chatbotId: selectedChatbotId,
                    forms: forms
                }),
            });

            if (response.ok) {
                setSaveStatus('success');
                localStorage.setItem(`chatbotForms_${selectedChatbotId}`, JSON.stringify(forms));
                setTimeout(() => setSaveStatus(''), 3000);
            } else {
                throw new Error('Failed to save forms');
            }
        } catch (error) {
            logger.error('Error saving forms:', error);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus(''), 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const addFormField = () => {
        setNewForm(prev => ({
            ...prev,
            fields: [...prev.fields, { name: '', type: 'text', required: true }]
        }));
    };

    const removeFormField = (index) => {
        setNewForm(prev => ({
            ...prev,
            fields: prev.fields.filter((_, i) => i !== index)
        }));
    };

    const updateFormField = (index, field, value) => {
        setNewForm(prev => ({
            ...prev,
            fields: prev.fields.map((f, i) =>
                i === index ? { ...f, [field]: value } : f
            )
        }));
    };

    const addForm = () => {
        if (!newForm.prompt.trim()) {
            alert('Please enter a form prompt');
            return;
        }

        if (newForm.fields.length === 0 || !newForm.fields[0].name.trim()) {
            alert('Please add at least one form field');
            return;
        }

        const stepExists = forms.some(form => form.step === newForm.step);
        if (stepExists) {
            alert(`Step ${newForm.step} is already used. Please choose a different step number.`);
            return;
        }

        const formToAdd = {
            id: Date.now().toString(),
            ...newForm,
            fields: newForm.fields.filter(field => field.name.trim() !== '')
        };

        setForms(prev => [...prev, formToAdd].sort((a, b) => a.step - b.step));

        setNewForm({
            prompt: '',
            fields: [{ name: '', type: 'text', required: true }],
            step: 0,
            enabled: true
        });

        setIsEditing(false);
    };

    const editForm = (index) => {
        setNewForm(forms[index]);
        setEditingIndex(index);
        setIsEditing(true);
    };

    const updateForm = () => {
        if (!newForm.prompt.trim()) {
            alert('Please enter a form prompt');
            return;
        }

        if (newForm.fields.length === 0 || !newForm.fields[0].name.trim()) {
            alert('Please add at least one form field');
            return;
        }

        const stepExists = forms.some((form, index) =>
            form.step === newForm.step && index !== editingIndex
        );
        if (stepExists) {
            alert(`Step ${newForm.step} is already used. Please choose a different step.`);
            return;
        }

        const updatedForms = forms.map((form, index) =>
            index === editingIndex
                ? { ...newForm, fields: newForm.fields.filter(field => field.name.trim() !== '') }
                : form
        ).sort((a, b) => a.step - b.step);

        setForms(updatedForms);

        setNewForm({
            prompt: '',
            fields: [{ name: '', type: 'text', required: true }],
            step: 0,
            enabled: true
        });

        setIsEditing(false);
        setEditingIndex(-1);
    };

    const deleteForm = (index) => {
        if (window.confirm('Are you sure you want to delete this form?')) {
            setForms(prev => prev.filter((_, i) => i !== index));
        }
    };

    const cancelEditing = () => {
        setNewForm({
            prompt: '',
            fields: [{ name: '', type: 'text', required: true }],
            step: 0,
            enabled: true
        });
        setIsEditing(false);
        setEditingIndex(-1);
    };

    const toggleFormStatus = (index) => {
        setForms(prev => prev.map((form, i) =>
            i === index ? { ...form, enabled: !form.enabled } : form
        ));
    };

    return (
        <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
            <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">

                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10 flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-lg font-bold tracking-tight text-slate-900">Forms Configuration</h2>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Collect structured data from users during conversations.</p>
                        </div>
                        {selectedChatbotId && forms.length > 0 && activeTab === 'builder' && (
                            <button
                                onClick={saveFormsToBackend}
                                disabled={isSaving}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg shadow-sm disabled:opacity-70 transition-all"
                            >
                                <Save size={14} />
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        )}
                    </div>

                    {/* Status Message */}
                    {saveStatus && (
                        <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${saveStatus === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                            {saveStatus === 'success' ? '✅ Saved successfully' : '❌ Save failed'}
                        </div>
                    )}

                    {/* Tabs */}
                    {selectedChatbotId && (
                        <div className="flex bg-slate-100/50 p-1 rounded-lg w-fit border border-slate-200/50">
                            <button
                                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'builder'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                                    }`}
                                onClick={() => setActiveTab('builder')}
                            >
                                <LayoutList size={14} />
                                Form Builder
                            </button>
                            <button
                                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'submissions'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                                    }`}
                                onClick={() => setActiveTab('submissions')}
                            >
                                <Database size={14} />
                                Submissions
                            </button>
                        </div>
                    )}
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
                    {selectedChatbotId ? (
                        <>
                            {activeTab === 'builder' && (
                                <div className="max-w-4xl mx-auto flex flex-col gap-6">

                                    {/* Edit / Create Form Section */}
                                    {isEditing ? (
                                        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                                    {editingIndex >= 0 ? <Edit3 size={16} /> : <Plus size={16} />}
                                                    {editingIndex >= 0 ? 'Edit Form' : 'Create New Form'}
                                                </h3>
                                                <button
                                                    onClick={cancelEditing}
                                                    className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 px-2 py-1 hover:bg-slate-50 rounded-md transition-colors"
                                                >
                                                    <X size={14} /> Cancel
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                {/* Left Column: Basic Info */}
                                                <div className="space-y-4">
                                                    <div className="space-y-1.5">
                                                        <label className="text-xs font-semibold text-slate-600">Prompt Message</label>
                                                        <textarea
                                                            value={newForm.prompt}
                                                            onChange={(e) => setNewForm(prev => ({ ...prev, prompt: e.target.value }))}
                                                            placeholder="e.g., Can I get your details?"
                                                            rows={3}
                                                            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800 resize-none placeholder:text-slate-400"
                                                        />
                                                        <p className="text-[10px] text-slate-400">Message shown before the form appears.</p>
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <label className="text-xs font-semibold text-slate-600">Step Number</label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={newForm.step}
                                                            onChange={(e) => setNewForm(prev => ({ ...prev, step: parseInt(e.target.value) || 0 }))}
                                                            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800"
                                                        />
                                                        <p className="text-[10px] text-slate-400">Shows after X messages (0 = start).</p>
                                                    </div>
                                                </div>

                                                {/* Right Column: Fields */}
                                                <div className="md:col-span-2 space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <label className="text-xs font-semibold text-slate-600">Form Fields</label>
                                                        <button
                                                            onClick={addFormField}
                                                            className="text-xs flex items-center gap-1 text-slate-600 hover:text-slate-900 font-medium bg-slate-50 px-2 py-1 rounded-md border border-slate-200 hover:bg-white"
                                                        >
                                                            <Plus size={12} /> Add Field
                                                        </button>
                                                    </div>

                                                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                                                        {newForm.fields.map((field, index) => (
                                                            <div key={index} className="flex gap-2 items-start bg-slate-50/50 p-2 rounded-lg border border-slate-100">
                                                                <div className="flex-1 space-y-2">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Field Label"
                                                                        value={field.name}
                                                                        onChange={(e) => updateFormField(index, 'name', e.target.value)}
                                                                        className="w-full px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:border-slate-400 text-slate-800"
                                                                    />
                                                                </div>
                                                                <div className="w-24">
                                                                    <select
                                                                        value={field.type}
                                                                        onChange={(e) => updateFormField(index, 'type', e.target.value)}
                                                                        className="w-full px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:border-slate-400 text-slate-800"
                                                                    >
                                                                        <option value="text">Text</option>
                                                                        <option value="email">Email</option>
                                                                        <option value="tel">Phone</option>
                                                                        <option value="number">Number</option>
                                                                        <option value="textarea">Long Text</option>
                                                                    </select>
                                                                </div>
                                                                <div className="flex items-center pt-2">
                                                                    <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-600 select-none">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={field.required}
                                                                            onChange={(e) => updateFormField(index, 'required', e.target.checked)}
                                                                            className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                                                                        />
                                                                        Req.
                                                                    </label>
                                                                </div>
                                                                {newForm.fields.length > 1 && (
                                                                    <button
                                                                        onClick={() => removeFormField(index)}
                                                                        className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-red-50 mt-0.5"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="pt-3 border-t border-slate-100 flex justify-end">
                                                <button
                                                    onClick={editingIndex >= 0 ? updateForm : addForm}
                                                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg shadow-sm flex items-center gap-2 transition-all"
                                                >
                                                    <Save size={14} />
                                                    {editingIndex >= 0 ? 'Update Form' : 'Create Form'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="text-sm font-bold text-slate-900">{forms.length} Active Form{forms.length !== 1 && 's'}</h3>
                                            <button
                                                onClick={() => setIsEditing(true)}
                                                className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium rounded-lg shadow-sm flex items-center gap-2 transition-all"
                                            >
                                                <Plus size={14} /> Add Form
                                            </button>
                                        </div>
                                    )}

                                    {/* Forms List */}
                                    <div className="space-y-4">
                                        {forms.length === 0 && !isEditing ? (
                                            <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                                                <Clipboard size={32} className="mx-auto text-slate-300 mb-3" />
                                                <h3 className="text-sm font-medium text-slate-900">No forms created yet</h3>
                                                <p className="text-xs text-slate-500 mt-1 max-w-[200px] mx-auto">Create a form to collect user information during the chat.</p>
                                                <button
                                                    onClick={() => setIsEditing(true)}
                                                    className="mt-4 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg shadow-sm hover:border-slate-300 transition-all inline-flex items-center gap-2"
                                                >
                                                    <Plus size={14} /> Create First Form
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 gap-4">
                                                {forms.map((form, index) => (
                                                    !isEditing && ( // Hide list if editing? Or show behind? Let's hide detailed cards if editing to reduce clutter or show them dimmed?
                                                        // Let's show them normally.
                                                        <div key={form.id || index} className={`bg-white border text-left border-slate-200 rounded-xl p-4 transition-all hover:shadow-md hover:border-slate-300 group ${!form.enabled ? 'opacity-60 grayscale' : ''}`}>
                                                            <div className="flex justify-between items-start mb-3">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="bg-slate-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                                                        Step {form.step}
                                                                    </span>
                                                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${form.enabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                                                        {form.enabled ? 'Active' : 'Disabled'}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <button
                                                                        onClick={() => editForm(index)}
                                                                        className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                                                                        title="Edit"
                                                                    >
                                                                        <Edit3 size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => toggleFormStatus(index)}
                                                                        className={`p-1.5 hover:bg-slate-100 rounded-lg transition-colors ${form.enabled ? 'text-slate-400 hover:text-orange-500' : 'text-slate-400 hover:text-green-500'}`}
                                                                        title={form.enabled ? 'Disable' : 'Enable'}
                                                                    >
                                                                        <Eye size={14} className={form.enabled ? '' : 'text-slate-300'} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => deleteForm(index)}
                                                                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                        title="Delete"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-3">
                                                                <div>
                                                                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Prompt</p>
                                                                    <p className="text-sm text-slate-700 font-medium line-clamp-2">{form.prompt}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Fields</p>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {form.fields.map((f, i) => (
                                                                            <span key={i} className="inline-flex items-center text-xs bg-slate-50 border border-slate-200 text-slate-600 px-2 py-1 rounded-md">
                                                                                {f.name}
                                                                                {f.required && <span className="text-red-400 ml-0.5">*</span>}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'submissions' && (
                                <FormSubmissions
                                    selectedChatbotId={selectedChatbotId}
                                    forms={forms}
                                />
                            )}
                        </>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-slate-400">
                            <MessageCircle size={48} className="mb-4 opacity-50" />
                            <p>Select a chatbot to manage forms</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Forms; 
